import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * AI Hub - Full-fledged Admin AI Assistant
 *
 * A comprehensive AI interface for admin operations with:
 * - Full-screen chat interface
 * - Action classification and security levels
 * - Confirmation modals for sensitive operations
 * - Integration with Financial Hub and Inventory Hub data
 * - Proactive alerts and summaries
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FaBrain, FaPaperPlane, FaRobot, FaUser, FaShieldAlt, FaExclamationTriangle, FaClipboardList, FaChartLine, FaGem, FaDollarSign, FaHistory, FaLightbulb, FaSearch, FaFileExport, FaSpinner, FaInfoCircle, FaCheck, FaTimes, FaClock } from 'react-icons/fa';
import { useAuthStore } from '../../stores/authStore';
import { apiClient } from '../../utils/apiClient';
// ═══════════════════════════════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════════════════════════════
const fadeIn = keyframes `
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;
const slideIn = keyframes `
  from { opacity: 0; transform: translateX(-20px); }
  to { opacity: 1; transform: translateX(0); }
`;
const pulse = keyframes `
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;
const typingDots = keyframes `
  0%, 60%, 100% { opacity: 0.3; }
  30% { opacity: 1; }
`;
const shimmer = keyframes `
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;
// ═══════════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════════════
const PageContainer = styled.div `
  display: flex;
  flex-direction: column;
  height: calc(100vh - 80px);
  max-width: 1400px;
  margin: 0 auto;
  padding: 1.5rem;
  gap: 1.5rem;
  overflow: hidden; /* Prevent page scroll */

  @media (max-width: 768px) {
    padding: 1rem;
    height: calc(100vh - 60px);
  }
`;
const PageHeader = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;
const HeaderLeft = styled.div `
  display: flex;
  align-items: center;
  gap: 1rem;
`;
const HeaderIcon = styled.div `
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
`;
const HeaderTitle = styled.div `
  h1 {
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
  }
  p {
    font-size: 0.85rem;
    color: var(--text-secondary);
    margin: 0;
  }
`;
const HeaderRight = styled.div `
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;
const SecurityBadge = styled.div `
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 8px;
  color: #22c55e;
  font-size: 0.8rem;
  font-weight: 500;

  svg {
    font-size: 14px;
  }
`;
const MainContent = styled.div `
  display: flex;
  gap: 1.5rem;
  flex: 1;
  min-height: 0;
  overflow: hidden; /* Contain children */

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;
const ChatSection = styled.div `
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  overflow: hidden;
  min-width: 0;
  min-height: 0; /* Allow flex shrinking */
  height: 100%; /* Fixed height within parent */
`;
const SidePanel = styled.div `
  width: 320px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    width: 100%;
    flex-direction: row;
    overflow-x: auto;
    padding-bottom: 0.5rem;
  }
`;
const PanelCard = styled.div `
  background: var(--bg-secondary);
  border: 1px solid ${props => props.$highlight ? '#f59e0b' : 'var(--border-color)'};
  border-radius: 12px;
  padding: 1rem;
  box-shadow: ${props => props.$highlight ? '0 0 0 1px rgba(245, 158, 11, 0.2)' : 'none'};

  @media (max-width: 1024px) {
    min-width: 280px;
    flex-shrink: 0;
  }
`;
const PanelTitle = styled.div `
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.75rem;
  font-size: 0.9rem;

  svg {
    color: #8b5cf6;
  }
`;
const QuickActionGrid = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
`;
const QuickActionBtn = styled.button `
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.75rem 0.5rem;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--text-primary);

  &:hover {
    border-color: #8b5cf6;
    background: rgba(139, 92, 246, 0.05);
  }

  svg {
    font-size: 18px;
    color: #8b5cf6;
  }

  span {
    font-size: 0.7rem;
    text-align: center;
  }
`;
const AlertItem = styled.div `
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.5rem;
  background: ${props => props.$type === 'critical' ? 'rgba(239, 68, 68, 0.1)' :
    props.$type === 'warning' ? 'rgba(245, 158, 11, 0.1)' :
        'rgba(59, 130, 246, 0.1)'};
  border-radius: 6px;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateX(4px);
  }

  &:last-child {
    margin-bottom: 0;
  }

  svg {
    color: ${props => props.$type === 'critical' ? '#ef4444' :
    props.$type === 'warning' ? '#f59e0b' :
        '#3b82f6'};
    font-size: 14px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .alert-text {
    font-size: 0.75rem;
    color: var(--text-primary);
    line-height: 1.4;
  }

  .alert-count {
    font-weight: 600;
  }
`;
// Chat Components
const ChatHeader = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(99, 102, 241, 0.05) 100%);
`;
const ChatStatus = styled.div `
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: var(--text-secondary);

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #22c55e;
  }
`;
const ConversationDropdown = styled.div `
  position: relative;
`;
const ConversationButton = styled.button `
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 6px 12px;
  font-size: 0.75rem;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #8b5cf6;
    color: var(--text-primary);
  }

  svg {
    font-size: 12px;
  }
`;
const ConversationMenu = styled.div `
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  width: 300px;
  max-height: 400px;
  overflow-y: auto;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 100;
  display: ${props => props.$show ? 'block' : 'none'};
  animation: ${fadeIn} 0.2s ease;
`;
const ConversationMenuHeader = styled.div `
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border-color);
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const ConversationMenuItem = styled.button `
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  padding: 0.75rem 1rem;
  background: ${props => props.$active ? 'rgba(139, 92, 246, 0.1)' : 'transparent'};
  border: none;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  text-align: left;
  transition: background 0.2s;

  &:hover {
    background: rgba(139, 92, 246, 0.05);
  }

  &:last-child {
    border-bottom: none;
  }

  .preview {
    font-size: 0.8rem;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.7rem;
    color: var(--text-secondary);
  }
`;
const EmptyConversations = styled.div `
  padding: 1.5rem;
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.85rem;
`;
const ChatMessages = styled.div `
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0; /* Prevents flex overflow */
`;
const MessageWrapper = styled.div `
  display: flex;
  flex-direction: ${props => props.$isUser ? 'row-reverse' : 'row'};
  gap: 0.75rem;
  animation: ${fadeIn} 0.3s ease;
`;
const MessageAvatar = styled.div `
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${props => props.$isUser
    ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
    : 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)'};
  color: white;
  font-size: 16px;
`;
const MessageContent = styled.div `
  max-width: 70%;
  min-width: 0; /* Important: allows flex child to shrink below content size */
  padding: 1rem 1.25rem;
  border-radius: ${props => props.$isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px'};
  background: ${props => props.$isUser ? '#3b82f6' : 'var(--bg-primary)'};
  color: ${props => props.$isUser ? 'white' : 'var(--text-primary)'};
  border: ${props => props.$isUser ? 'none' : '1px solid var(--border-color)'};
  line-height: 1.6;
  font-size: 0.9rem;
  overflow: hidden; /* Contain overflowing content */

  /* Markdown Styling */
  h1, h2, h3, h4 {
    margin: 0.75rem 0 0.5rem 0;
    font-weight: 600;
    color: ${props => props.$isUser ? 'white' : '#8b5cf6'};
  }

  h1 { font-size: 1.3rem; }
  h2 { font-size: 1.15rem; }
  h3 { font-size: 1.05rem; }
  h4 { font-size: 0.95rem; }

  p {
    margin: 0.5rem 0;
  }

  strong {
    font-weight: 600;
    color: ${props => props.$isUser ? 'white' : '#8b5cf6'};
  }

  em {
    font-style: italic;
    color: ${props => props.$isUser ? 'rgba(255,255,255,0.9)' : 'var(--text-secondary)'};
  }

  ul, ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }

  li {
    margin: 0.25rem 0;
  }

  blockquote {
    border-left: 3px solid ${props => props.$isUser ? 'rgba(255,255,255,0.5)' : '#8b5cf6'};
    margin: 0.5rem 0;
    padding-left: 1rem;
    color: ${props => props.$isUser ? 'rgba(255,255,255,0.9)' : 'var(--text-secondary)'};
  }

  pre {
    background: ${props => props.$isUser ? 'rgba(0,0,0,0.2)' : 'var(--bg-secondary)'};
    padding: 0.75rem;
    border-radius: 8px;
    overflow-x: auto;
    margin: 0.5rem 0;
    font-size: 0.8rem;
  }

  code {
    font-family: 'Fira Code', monospace;
    background: ${props => props.$isUser ? 'rgba(0,0,0,0.2)' : 'rgba(139, 92, 246, 0.1)'};
    padding: 0.15rem 0.4rem;
    border-radius: 4px;
    font-size: 0.85em;
  }

  pre code {
    background: none;
    padding: 0;
  }

  hr {
    border: none;
    border-top: 1px solid ${props => props.$isUser ? 'rgba(255,255,255,0.3)' : 'var(--border-color)'};
    margin: 1rem 0;
  }

  a {
    color: ${props => props.$isUser ? '#93c5fd' : '#3b82f6'};
    text-decoration: underline;
  }

  /* Table Styling - Responsive and Clean */
  .table-wrapper {
    overflow-x: auto;
    margin: 1rem 0;
    border-radius: 8px;
    border: 1px solid ${props => props.$isUser ? 'rgba(255,255,255,0.2)' : 'var(--border-color)'};
    max-width: 100%;
  }

  table {
    border-collapse: collapse;
    width: max-content;
    min-width: 100%;
    font-size: 0.8rem;
    background: ${props => props.$isUser ? 'rgba(0,0,0,0.1)' : 'var(--bg-secondary)'};
    border-radius: 8px;
    overflow: hidden;
  }

  thead {
    background: ${props => props.$isUser ? 'rgba(0,0,0,0.3)' : 'rgba(139, 92, 246, 0.15)'};
  }

  th {
    padding: 0.6rem 0.75rem;
    text-align: left;
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${props => props.$isUser ? 'rgba(255,255,255,0.9)' : '#8b5cf6'};
    border-bottom: 2px solid ${props => props.$isUser ? 'rgba(255,255,255,0.2)' : 'rgba(139, 92, 246, 0.3)'};
    white-space: nowrap;
  }

  td {
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid ${props => props.$isUser ? 'rgba(255,255,255,0.1)' : 'var(--border-color)'};
    color: ${props => props.$isUser ? 'rgba(255,255,255,0.95)' : 'var(--text-primary)'};
  }

  tbody tr:hover {
    background: ${props => props.$isUser ? 'rgba(255,255,255,0.05)' : 'rgba(139, 92, 246, 0.05)'};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  /* Highlight price and stock columns */
  td:nth-child(1) {
    font-weight: 600;
    color: ${props => props.$isUser ? '#93c5fd' : '#3b82f6'};
  }
`;
const TypingIndicator = styled.div `
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 16px 16px 16px 4px;
  width: fit-content;

  .dots {
    display: flex;
    gap: 4px;

    span {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #8b5cf6;
      animation: ${typingDots} 1.4s infinite;

      &:nth-child(2) { animation-delay: 0.2s; }
      &:nth-child(3) { animation-delay: 0.4s; }
    }
  }
`;
// Action Proposal Card - Shows when AI proposes an action requiring approval
const ActionProposalCard = styled.div `
  margin-top: 1rem;
  padding: 1.25rem;
  background: ${props => props.$status === 'approved' ? 'rgba(34, 197, 94, 0.1)' :
    props.$status === 'declined' ? 'rgba(239, 68, 68, 0.1)' :
        props.$status === 'expired' ? 'rgba(156, 163, 175, 0.1)' :
            'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(99, 102, 241, 0.05) 100%)'};
  border: 2px solid ${props => props.$status === 'approved' ? '#22c55e' :
    props.$status === 'declined' ? '#ef4444' :
        props.$status === 'expired' ? '#9ca3af' :
            '#8b5cf6'};
  border-radius: 12px;

  .proposal-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
    font-weight: 600;
    color: ${props => props.$status === 'approved' ? '#22c55e' :
    props.$status === 'declined' ? '#ef4444' :
        props.$status === 'expired' ? '#9ca3af' :
            '#8b5cf6'};

    svg {
      font-size: 1.25rem;
    }
  }

  .proposal-operation {
    display: inline-block;
    padding: 0.25rem 0.75rem;
    background: rgba(139, 92, 246, 0.2);
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    color: #8b5cf6;
    margin-bottom: 0.75rem;
  }

  .proposal-description {
    font-size: 0.95rem;
    color: var(--text-primary);
    margin-bottom: 1rem;
    line-height: 1.5;
  }

  .proposal-params {
    background: var(--bg-secondary);
    border-radius: 8px;
    padding: 0.75rem;
    margin-bottom: 1rem;
    font-size: 0.85rem;

    .param-row {
      display: flex;
      justify-content: space-between;
      padding: 0.25rem 0;
      border-bottom: 1px solid var(--border-color);

      &:last-child {
        border-bottom: none;
      }

      .param-key {
        color: var(--text-secondary);
        font-weight: 500;
      }

      .param-value {
        color: var(--text-primary);
        font-weight: 600;
      }
    }
  }

  .proposal-expiry {
    font-size: 0.75rem;
    color: var(--text-secondary);
    margin-bottom: 1rem;
  }

  .proposal-status {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    font-size: 0.9rem;
    color: ${props => props.$status === 'approved' ? '#22c55e' :
    props.$status === 'declined' ? '#ef4444' :
        '#9ca3af'};

    svg {
      font-size: 1rem;
    }
  }
`;
const ActionButtons = styled.div `
  display: flex;
  gap: 0.75rem;
`;
const ApproveButton = styled.button `
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    font-size: 1rem;
  }
`;
const DeclineButton = styled.button `
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: transparent;
  color: #ef4444;
  border: 2px solid #ef4444;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: rgba(239, 68, 68, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    font-size: 1rem;
  }
`;
const ChatInputContainer = styled.div `
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
  flex-shrink: 0; /* Prevents container from shrinking */
`;
const InputWrapper = styled.div `
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  background: var(--bg-primary);
  border: 2px solid var(--border-color);
  border-radius: 12px;
  padding: 0.75rem;
  transition: border-color 0.2s;

  &:focus-within {
    border-color: #8b5cf6;
  }
`;
const TextInput = styled.textarea `
  flex: 1;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 0.9rem;
  resize: none;
  min-height: 24px;
  max-height: 120px;
  line-height: 1.5;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: var(--text-secondary);
  }
`;
const SendButton = styled.button `
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    font-size: 18px;
  }
`;
const WelcomeMessage = styled.div `
  text-align: center;
  padding: 2rem;
  animation: ${fadeIn} 0.5s ease;

  .welcome-icon {
    width: 64px;
    height: 64px;
    border-radius: 20px;
    background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
    font-size: 32px;
    color: white;
  }

  h2 {
    color: var(--text-primary);
    font-size: 1.25rem;
    margin-bottom: 0.5rem;
  }

  p {
    color: var(--text-secondary);
    font-size: 0.9rem;
    max-width: 400px;
    margin: 0 auto;
  }
`;
const SuggestionsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
  margin-top: 1.5rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;
const SuggestionCard = styled.button `
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;

  &:hover {
    border-color: #8b5cf6;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.1);
  }

  svg {
    font-size: 20px;
    color: #8b5cf6;
    flex-shrink: 0;
  }

  .text {
    font-size: 0.85rem;
    color: var(--text-primary);
    line-height: 1.4;
  }
`;
const AIHub = () => {
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [conversationId, setConversationId] = useState(null);
    const [conversationList, setConversationList] = useState([]);
    const [showConversationList, setShowConversationList] = useState(false);
    const [loadingConversations, setLoadingConversations] = useState(false);
    const chatContainerRef = useRef(null);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const { user } = useAuthStore();
    // Load conversation list
    const loadConversationList = useCallback(async () => {
        if (!user?.email)
            return;
        setLoadingConversations(true);
        try {
            const response = await apiClient.get(`ai-conversations/list?userId=${encodeURIComponent(user.email)}`);
            if (response.data?.conversations) {
                setConversationList(response.data.conversations);
            }
        }
        catch (err) {
            console.error('Failed to load conversations:', err);
        }
        finally {
            setLoadingConversations(false);
        }
    }, [user?.email]);
    // Load a specific conversation
    const loadConversation = useCallback(async (convId) => {
        if (!user?.email)
            return;
        setIsLoading(true);
        try {
            const response = await apiClient.get(`ai-conversations/load?id=${encodeURIComponent(convId)}&userId=${encodeURIComponent(user.email)}`);
            if (response.data?.conversation) {
                const conv = response.data.conversation;
                setConversationId(conv.id);
                // Convert stored messages to Message format
                setMessages(conv.messages.map((m) => ({
                    id: m.id || `msg-${Date.now()}-${Math.random()}`,
                    role: m.role,
                    content: m.content,
                    timestamp: m.timestamp
                })));
                setShowConversationList(false);
            }
        }
        catch (err) {
            console.error('Failed to load conversation:', err);
        }
        finally {
            setIsLoading(false);
        }
    }, [user?.email]);
    // Load conversations on mount
    useEffect(() => {
        if (user?.email) {
            loadConversationList();
        }
    }, [user?.email, loadConversationList]);
    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowConversationList(false);
            }
        };
        if (showConversationList) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showConversationList]);
    // Mock alerts - will be replaced with real data
    const [alerts] = useState([
        { id: '1', type: 'critical', text: 'overdue memos', count: 3, query: 'Show me overdue memos' },
        { id: '2', type: 'warning', text: 'pending approvals', count: 5, query: 'Show pending memo approvals' },
        { id: '3', type: 'info', text: 'diamonds aged 90+ days', count: 12, query: 'Show slow-moving inventory over 90 days' },
    ]);
    // Scroll chat container to bottom when messages change (not the page)
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages]);
    // Auto-resize textarea without flickering
    const handleInputChange = (e) => {
        const textarea = e.target;
        setInputValue(textarea.value);
        // Only resize if content changes require it
        // Reset to min-height first to get accurate scrollHeight
        textarea.style.height = '24px';
        const newHeight = Math.min(Math.max(textarea.scrollHeight, 24), 120);
        textarea.style.height = `${newHeight}px`;
    };
    // Handle sending message - uses AI API with system prompt
    const handleSendMessage = useCallback(async (messageText) => {
        const text = messageText || inputValue.trim();
        if (!text || isLoading)
            return;
        // Clear input
        setInputValue('');
        if (inputRef.current) {
            inputRef.current.style.height = 'auto';
        }
        // Add user message
        const userMessage = {
            id: `msg-${Date.now()}`,
            role: 'user',
            content: text,
            timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, userMessage]);
        setIsLoading(true);
        try {
            // Build conversation history for the API
            const conversationHistory = [
                ...messages.map(m => ({
                    role: m.role,
                    content: m.content
                })),
                { role: 'user', content: text }
            ];
            // Call the Multi-Agent AI API with conversation persistence
            // If conversationId is null, this is a new conversation
            const response = await apiClient.post('ai-agents', {
                message: text,
                conversationId: conversationId,
                userId: user?.email || 'Admin',
                newConversation: conversationId === null && messages.length === 0
            });
            // Store conversation ID for future messages
            if (response.data?.conversationId && !conversationId) {
                setConversationId(response.data.conversationId);
            }
            const assistantMessage = {
                id: `msg-${Date.now()}`,
                role: 'assistant',
                content: response.data?.content || response.data?.message || 'I apologize, but I encountered an issue processing your request. Please try again.',
                timestamp: new Date().toISOString(),
                // Include action proposal if present
                actionProposal: response.data?.actionProposal ? {
                    ...response.data.actionProposal,
                    status: 'pending'
                } : undefined
            };
            setMessages(prev => [...prev, assistantMessage]);
        }
        catch (error) {
            console.error('Error processing message:', error);
            // Add error message
            const errorMessage = {
                id: `msg-${Date.now()}`,
                role: 'assistant',
                content: `I apologize, but I encountered an error: ${error.message || 'Unable to process your request'}. Please try again.`,
                timestamp: new Date().toISOString()
            };
            setMessages(prev => [...prev, errorMessage]);
        }
        finally {
            setIsLoading(false);
        }
    }, [inputValue, isLoading, messages, user]);
    // Handle key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };
    // Handle action approval
    const handleApproveAction = useCallback(async (messageId, actionProposal) => {
        try {
            // Update UI to show loading state
            setMessages(prev => prev.map(msg => msg.id === messageId && msg.actionProposal
                ? { ...msg, actionProposal: { ...msg.actionProposal, status: 'approved' } }
                : msg));
            // Call the AI Operations API to execute the action
            const response = await apiClient.post('ai-operations?action=execute', {
                actionId: actionProposal.actionId,
                signature: actionProposal.signature,
                userId: user?.email || 'admin'
            });
            if (response.data?.success) {
                // Add success message
                const successMessage = {
                    id: `msg-${Date.now()}`,
                    role: 'assistant',
                    content: `**Action Completed Successfully**\n\n${response.data.result?.message || 'The operation was executed successfully.'}\n\n${response.data.result?.memoId ? `**Memo ID:** ${response.data.result.memoId}` : ''}`,
                    timestamp: new Date().toISOString()
                };
                setMessages(prev => [...prev, successMessage]);
            }
            else {
                throw new Error(response.data?.error || 'Action execution failed');
            }
        }
        catch (error) {
            console.error('Error executing action:', error);
            // Revert status and show error
            setMessages(prev => prev.map(msg => msg.id === messageId && msg.actionProposal
                ? { ...msg, actionProposal: { ...msg.actionProposal, status: 'pending' } }
                : msg));
            const errorMessage = {
                id: `msg-${Date.now()}`,
                role: 'assistant',
                content: `**Action Failed**\n\n${error.message || 'Unable to execute the action. Please try again.'}`,
                timestamp: new Date().toISOString()
            };
            setMessages(prev => [...prev, errorMessage]);
        }
    }, [user]);
    // Handle action decline
    const handleDeclineAction = useCallback(async (messageId, actionProposal) => {
        try {
            // Call API to cancel the action
            await apiClient.post('ai-operations?action=cancel', {
                actionId: actionProposal.actionId
            });
        }
        catch (error) {
            console.error('Error canceling action:', error);
        }
        // Update UI
        setMessages(prev => prev.map(msg => msg.id === messageId && msg.actionProposal
            ? { ...msg, actionProposal: { ...msg.actionProposal, status: 'declined' } }
            : msg));
        // Add decline message
        const declineMessage = {
            id: `msg-${Date.now()}`,
            role: 'assistant',
            content: `**Action Cancelled**\n\nThe proposed action has been declined. Is there anything else I can help you with?`,
            timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, declineMessage]);
    }, []);
    // Quick actions
    const quickActions = [
        { icon: _jsx(FaClipboardList, {}), label: 'Pending Memos', query: 'Show me pending memo approvals' },
        { icon: _jsx(FaDollarSign, {}), label: 'Cash Flow', query: 'What is our current cash flow?' },
        { icon: _jsx(FaGem, {}), label: 'Inventory', query: 'Show inventory summary' },
        { icon: _jsx(FaExclamationTriangle, {}), label: 'Overdue', query: 'Show all overdue items' },
    ];
    const suggestions = [
        { icon: _jsx(FaSearch, {}), text: 'Show me pending memo approvals' },
        { icon: _jsx(FaChartLine, {}), text: 'What is our cash flow this month?' },
        { icon: _jsx(FaGem, {}), text: 'Find slow-moving diamonds over 90 days' },
        { icon: _jsx(FaFileExport, {}), text: 'Generate overdue report' },
    ];
    return (_jsxs(PageContainer, { children: [_jsxs(PageHeader, { children: [_jsxs(HeaderLeft, { children: [_jsx(HeaderIcon, { children: _jsx(FaBrain, {}) }), _jsxs(HeaderTitle, { children: [_jsx("h1", { children: "AI Hub" }), _jsx("p", { children: "Your intelligent admin assistant" })] })] }), _jsx(HeaderRight, { children: _jsxs(SecurityBadge, { children: [_jsx(FaShieldAlt, {}), "Secure Mode Active"] }) })] }), _jsxs(MainContent, { children: [_jsxs(ChatSection, { children: [_jsxs(ChatHeader, { children: [_jsxs(ChatStatus, { children: [_jsx("span", { className: "dot" }), "AI Assistant Ready"] }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '0.75rem' }, children: [_jsxs("span", { style: { fontSize: '0.75rem', color: 'var(--text-secondary)' }, children: [messages.length, " messages"] }), _jsxs(ConversationDropdown, { ref: dropdownRef, children: [_jsxs(ConversationButton, { onClick: () => {
                                                            setShowConversationList(!showConversationList);
                                                            if (!showConversationList)
                                                                loadConversationList();
                                                        }, children: [_jsx(FaHistory, {}), "History"] }), _jsxs(ConversationMenu, { "$show": showConversationList, children: [_jsxs(ConversationMenuHeader, { children: [_jsx("span", { children: "Previous Conversations" }), _jsx("button", { onClick: () => setShowConversationList(false), style: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }, children: _jsx(FaTimes, {}) })] }), loadingConversations ? (_jsxs(EmptyConversations, { children: [_jsx(FaSpinner, { className: "spin", style: { marginRight: '0.5rem' } }), "Loading..."] })) : conversationList.length === 0 ? (_jsx(EmptyConversations, { children: "No previous conversations" })) : (conversationList.map(conv => (_jsxs(ConversationMenuItem, { "$active": conv.id === conversationId, onClick: () => loadConversation(conv.id), children: [_jsx("span", { className: "preview", children: conv.preview }), _jsxs("span", { className: "meta", children: [_jsx(FaClock, {}), new Date(conv.lastMessageAt).toLocaleDateString(), ' - ', conv.messageCount, " messages"] })] }, conv.id))))] })] }), _jsx("button", { onClick: () => {
                                                    setMessages([]);
                                                    setConversationId(null);
                                                    setShowConversationList(false);
                                                }, style: {
                                                    padding: '6px 12px',
                                                    fontSize: '0.75rem',
                                                    background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    color: 'white',
                                                    cursor: 'pointer',
                                                    fontWeight: 500
                                                }, children: "New Chat" })] })] }), _jsxs(ChatMessages, { ref: chatContainerRef, children: [messages.length === 0 ? (_jsxs(WelcomeMessage, { children: [_jsx("div", { className: "welcome-icon", children: _jsx(FaBrain, {}) }), _jsx("h2", { children: "Welcome to AI Hub" }), _jsx("p", { children: "I can help you manage memos, analyze inventory, track financials, and more. All sensitive actions require your confirmation." }), _jsx(SuggestionsGrid, { children: suggestions.map((suggestion, index) => (_jsxs(SuggestionCard, { onClick: () => handleSendMessage(suggestion.text), children: [suggestion.icon, _jsx("span", { className: "text", children: suggestion.text })] }, index))) })] })) : (messages.map(message => (_jsxs(MessageWrapper, { "$isUser": message.role === 'user', children: [_jsx(MessageAvatar, { "$isUser": message.role === 'user', children: message.role === 'user' ? _jsx(FaUser, {}) : _jsx(FaRobot, {}) }), _jsx(MessageContent, { "$isUser": message.role === 'user', children: message.role === 'user' ? (_jsx("div", { style: { whiteSpace: 'pre-wrap' }, children: message.content })) : (_jsxs(_Fragment, { children: [_jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], children: message.content }), message.actionProposal && (_jsxs(ActionProposalCard, { "$status": message.actionProposal.status, children: [_jsx("div", { className: "proposal-header", children: message.actionProposal.status === 'approved' ? (_jsxs(_Fragment, { children: [_jsx(FaCheck, {}), " Action Approved"] })) : message.actionProposal.status === 'declined' ? (_jsxs(_Fragment, { children: [_jsx(FaTimes, {}), " Action Declined"] })) : message.actionProposal.status === 'expired' ? (_jsxs(_Fragment, { children: [_jsx(FaClock, {}), " Action Expired"] })) : (_jsxs(_Fragment, { children: [_jsx(FaShieldAlt, {}), " Action Requires Approval"] })) }), _jsx("div", { className: "proposal-operation", children: message.actionProposal.operation.replace(/_/g, ' ') }), _jsx("div", { className: "proposal-description", children: message.actionProposal.description }), _jsx("div", { className: "proposal-params", children: Object.entries(message.actionProposal.params).map(([key, value]) => (_jsxs("div", { className: "param-row", children: [_jsxs("span", { className: "param-key", children: [key.replace(/([A-Z])/g, ' $1').trim(), ":"] }), _jsx("span", { className: "param-value", children: typeof value === 'object' ? JSON.stringify(value) : String(value) })] }, key))) }), message.actionProposal.status === 'pending' && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "proposal-expiry", children: [_jsx(FaClock, { style: { marginRight: '0.25rem' } }), "Expires in ", message.actionProposal.expiresIn] }), _jsxs(ActionButtons, { children: [_jsxs(ApproveButton, { onClick: () => handleApproveAction(message.id, message.actionProposal), disabled: isLoading, children: [_jsx(FaCheck, {}), " Approve"] }), _jsxs(DeclineButton, { onClick: () => handleDeclineAction(message.id, message.actionProposal), disabled: isLoading, children: [_jsx(FaTimes, {}), " Decline"] })] })] })), message.actionProposal.status !== 'pending' && (_jsxs("div", { className: "proposal-status", children: [message.actionProposal.status === 'approved' && _jsxs(_Fragment, { children: [_jsx(FaCheck, {}), " This action was approved and executed"] }), message.actionProposal.status === 'declined' && _jsxs(_Fragment, { children: [_jsx(FaTimes, {}), " This action was declined"] }), message.actionProposal.status === 'expired' && _jsxs(_Fragment, { children: [_jsx(FaClock, {}), " This action has expired"] })] }))] }))] })) })] }, message.id)))), isLoading && (_jsxs(MessageWrapper, { "$isUser": false, children: [_jsx(MessageAvatar, { "$isUser": false, children: _jsx(FaRobot, {}) }), _jsx(TypingIndicator, { children: _jsxs("div", { className: "dots", children: [_jsx("span", {}), _jsx("span", {}), _jsx("span", {})] }) })] }))] }), _jsx(ChatInputContainer, { children: _jsxs(InputWrapper, { children: [_jsx(TextInput, { ref: inputRef, value: inputValue, onChange: handleInputChange, onKeyPress: handleKeyPress, placeholder: "Ask me anything about your business...", rows: 1 }), _jsx(SendButton, { onClick: () => handleSendMessage(), disabled: !inputValue.trim() || isLoading, children: isLoading ? _jsx(FaSpinner, { className: "spin" }) : _jsx(FaPaperPlane, {}) })] }) })] }), _jsxs(SidePanel, { children: [_jsxs(PanelCard, { children: [_jsxs(PanelTitle, { children: [_jsx(FaLightbulb, {}), " Quick Actions"] }), _jsx(QuickActionGrid, { children: quickActions.map((action, index) => (_jsxs(QuickActionBtn, { onClick: () => handleSendMessage(action.query), children: [action.icon, _jsx("span", { children: action.label })] }, index))) })] }), _jsxs(PanelCard, { "$highlight": alerts.some(a => a.type === 'critical'), children: [_jsxs(PanelTitle, { children: [_jsx(FaExclamationTriangle, {}), " Attention Needed"] }), alerts.map(alert => (_jsxs(AlertItem, { "$type": alert.type, onClick: () => alert.query && handleSendMessage(alert.query), children: [alert.type === 'critical' ? _jsx(FaExclamationTriangle, {}) :
                                                alert.type === 'warning' ? _jsx(FaInfoCircle, {}) :
                                                    _jsx(FaInfoCircle, {}), _jsxs("span", { className: "alert-text", children: [_jsx("span", { className: "alert-count", children: alert.count }), " ", alert.text] })] }, alert.id)))] }), _jsxs(PanelCard, { children: [_jsxs(PanelTitle, { children: [_jsx(FaHistory, {}), " Recent Messages"] }), messages.length === 0 ? (_jsx("div", { style: { fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'center', padding: '1rem' }, children: "No messages yet" })) : (messages.slice(-5).reverse().map(msg => (_jsxs(AlertItem, { "$type": msg.role === 'user' ? 'info' : 'warning', children: [msg.role === 'user' ? _jsx(FaUser, {}) : _jsx(FaRobot, {}), _jsxs("span", { className: "alert-text", children: [msg.content.substring(0, 40), msg.content.length > 40 ? '...' : ''] })] }, msg.id))))] })] })] })] }));
};
export default AIHub;
